import { Component, inject } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { Platform } from '@ionic/angular/standalone';
import { Capacitor } from '@capacitor/core';
import { isMobilePlatform } from '../../services/mobile-platform.util';
import {
  mobileAccessDecision, detectBrowser, GateDecision, DevicePlatform,
  INSTALL_TITLE, IOS_STEPS, ANDROID_INTRO, ANDROID_BUTTON, ANDROID_FALLBACK_STEPS,
  ESCAPE_IOS, ESCAPE_ANDROID, UNAVAILABLE_TITLE, UNAVAILABLE_BODY,
} from '../../services/mobile-access-rules';

/**
 * The mobile access gate — a full-screen cover shown when the rule says this visitor may not use the
 * app in a browser.
 *
 * DELIBERATELY THIN. Every decision belongs to mobile-access-rules.ts, which is testable without a
 * browser; this file renders the answer and nothing more. If logic starts accumulating here, it is in
 * the wrong file.
 *
 * It sits in the same slot as the loading overlay in app.component.html, which is what makes it cover
 * every route including the pre-auth landing page — a visitor should be told to install before being
 * shown a page they cannot use.
 */
@Component({
  selector: 'app-mobile-access-gate',
  standalone: true,
  imports: [NgIf, NgFor],
  templateUrl: './mobile-access-gate.component.html',
  styleUrl: './mobile-access-gate.component.css',
})
export class MobileAccessGateComponent {
  private platform = inject(Platform);

  readonly decision: GateDecision;
  readonly appName: string;
  readonly iconUrl: string | null;

  // The copy lives with the rule; the template only reads it.
  readonly installTitle = INSTALL_TITLE;
  readonly iosSteps = IOS_STEPS;
  readonly androidIntro = ANDROID_INTRO;
  readonly androidButton = ANDROID_BUTTON;
  readonly androidFallbackSteps = ANDROID_FALLBACK_STEPS;
  readonly unavailableTitle = UNAVAILABLE_TITLE;
  readonly unavailableBody = UNAVAILABLE_BODY;

  /** Chrome's own install prompt, captured when it fires. Null until then — and on iOS, forever. */
  private installPrompt: any = null;

  /** True once a tap has proven the button has nothing to trigger — so it stops being a dead end. */
  showAndroidSteps = false;

  constructor() {
    const cfg = (window as any).ZAVO_RUNTIME || {};
    const ua = navigator.userAgent || '';
    this.appName = cfg.appName || '';
    this.iconUrl = cfg.iconUrl || null;
    this.decision = mobileAccessDecision({
      mode: cfg.mobileAccessMode,
      isMobile: isMobilePlatform(this.platform),
      native: Capacitor.isNativePlatform(),
      standalone: (navigator as any).standalone === true
        || window.matchMedia?.('(display-mode: standalone)')?.matches === true,
      platform: this.devicePlatform(),
      browser: detectBrowser(ua),
    });

    // Chrome fires this when the site meets its installability criteria. Capturing it is what lets us
    // offer one tap instead of a menu hunt; iOS has no equivalent event, which is why that platform
    // gets written instructions and no button.
    //
    // ONLY WHEN THE GATE IS ACTUALLY SHOWING THE INSTALL SCREEN (M2, final review). This constructor
    // runs on every boot — mode `off`, the editor itself, an already-installed app — and attaching
    // (and preventDefault-ing) this listener unconditionally suppressed Chrome's OWN install
    // promotion on every one of those, including projects this plan is supposed to leave untouched.
    if (this.decision.screen === 'install') {
      window.addEventListener('beforeinstallprompt', (e: any) => {
        e.preventDefault();
        this.installPrompt = e;
      });
    }
  }

  /**
   * Which device this is, from Ionic's Platform — the SAME source isMobilePlatform() reads.
   * Deliberately not a second user-agent parse: two definitions of "which device" drifting apart is
   * the bug mobile-platform.util.ts exists to prevent, and it would show up as the gate disagreeing
   * with which pages the app chose.
   */
  private devicePlatform(): DevicePlatform {
    if (this.platform.is('android')) return 'android';
    if (this.platform.is('ios')) return 'ios';
    return 'other';
  }

  get escapeLine(): string {
    return this.decision.screen === 'install' && this.decision.platform === 'android'
      ? ESCAPE_ANDROID : ESCAPE_IOS;
  }

  /**
   * Never throws: a failed prompt must leave the instructions on screen, not break the page.
   *
   * No captured prompt means Chrome never fired beforeinstallprompt for this visitor — criteria not
   * yet met, service worker not yet activated, or the event already spent. A silent no-op there is a
   * dead end: the button looked tappable and did nothing. Falling back to written steps is what keeps
   * Android from being worse off than iOS merely because iOS never had a one-tap option to begin with.
   */
  async install(): Promise<void> {
    if (!this.installPrompt) {
      this.showAndroidSteps = true;
      return;
    }
    try {
      await this.installPrompt.prompt();
    } catch {
      // A rejected prompt leaves the user exactly as stuck as no prompt at all.
      this.showAndroidSteps = true;
    }
  }
}
